import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Send, 
  Bot, 
  User, 
  Loader2, 
  Sparkles,
  Wifi,
  WifiOff
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ragAPI, RAGChatResponse } from "@/lib/api";
import { cn } from "@/lib/utils";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { io, Socket } from 'socket.io-client';
import { Badge } from "@/components/ui/badge";
import { RAGChatProps } from "@/types";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:5000';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  streaming?: boolean;
}

interface ExtendedRAGChatProps extends RAGChatProps {
  isAdmin?: boolean;
  showHeader?: boolean;
  className?: string;
}

export default function RAGChat({ className, isAdmin = false, showHeader = true, context, initialPrompt }: ExtendedRAGChatProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: isAdmin 
        ? t('ragChat.adminWelcome')
        : t('ragChat.userWelcome'),
      timestamp: new Date(),
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const currentStreamingContentRef = useRef<string>('');

  // Initialize WebSocket connection
  useEffect(() => {
    console.log('🔌 Initializing RAG WebSocket connection...');
    
    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
      autoConnect: true,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('✅ RAG Socket connected:', socket.id);
      setIsConnected(true);
    });

    socket.on('disconnect', (reason) => {
      console.log('❌ RAG Socket disconnected:', reason);
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('❌ RAG Socket connection error:', error.message);
      setIsConnected(false);
    });

    // RAG Stream events
    socket.on('rag_stream_start', (data: { question: string }) => {
      console.log('📡 RAG stream started:', data.question);
      setIsStreaming(true);
      setIsLoading(false); // Hide standalone loading indicator
      currentStreamingContentRef.current = '';
      
      // Add placeholder assistant message
      setMessages(prev => [...prev, {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        streaming: true,
      }]);
      scrollToBottom();
    });

    socket.on('rag_stream_chunk', (data: { chunk: string }) => {
      console.log('📦 RAG chunk received:', data.chunk.substring(0, 30) + '...');
      currentStreamingContentRef.current += data.chunk;
      
      // Update last message with accumulated content
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage && lastMessage.streaming) {
          lastMessage.content = currentStreamingContentRef.current;
        }
        return newMessages;
      });
      
      // Auto-scroll during streaming
      scrollToBottom();
    });

    socket.on('rag_stream_complete', (data: { question: string; response: string; sources: string[] }) => {
      console.log('✅ RAG stream completed. Sources:', data.sources?.length || 0);
      setIsStreaming(false);
      setIsLoading(false);
      
      // Finalize the message with sources
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage && lastMessage.streaming) {
          lastMessage.content = data.response;
          lastMessage.streaming = false;
        }
        return newMessages;
      });
      
      currentStreamingContentRef.current = '';
    });

    socket.on('rag_stream_error', (data: { error: string }) => {
      console.error('❌ RAG stream error:', data.error);
      setIsStreaming(false);
      setIsLoading(false);
      
      toast({
        title: t('ragChat.error'),
        description: data.error,
        variant: "destructive",
      });
      
      // Remove incomplete streaming message
      setMessages(prev => prev.filter(msg => !msg.streaming));
    });

    // Cleanup on unmount
    return () => {
      console.log('🔌 Disconnecting RAG socket...');
      socket.disconnect();
    };
  }, [t, toast]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading || !isConnected) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    scrollToBottom();

    try {
      // Send question via WebSocket
      console.log('📤 Sending RAG question via WebSocket...');
      socketRef.current?.emit('rag_question', {
        question: userMessage.content,
        n_results: 5
      });
    } catch (error: any) {
      console.error('❌ Failed to send RAG question:', error);
      
      toast({
        title: t('ragChat.error'),
        description: getLocalizedErrorMessage(error, 'ragChat.failedResponse'),
        variant: "destructive",
      });

      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: t('ragChat.errorMessage'),
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Card className={cn("flex flex-col bg-card/50 backdrop-blur-sm border-border/50", className)}>
      {/* Header */}
      {showHeader && (
        <div className="p-5 border-b border-border/50 bg-gradient-to-r from-primary/5 to-transparent">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 shadow-sm">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
                  {isAdmin ? t('ragChat.adminTitle') : t('ragChat.userTitle')}
                </h3>
                <p className="text-xs text-muted-foreground font-medium">
                  {t('ragChat.poweredBy')}
                </p>
              </div>
            </div>
            
            {/* Connection Status */}
            <Badge 
              variant={isConnected ? "default" : "secondary"} 
              className={cn(
                "gap-1.5 px-3 py-1 shadow-sm transition-all",
                isConnected && "bg-green-500/90 hover:bg-green-500"
              )}
            >
              {isConnected ? (
                <>
                  <Wifi className="w-3.5 h-3.5" />
                  <span className="text-xs font-semibold">Connected</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-3.5 h-3.5" />
                  <span className="text-xs font-semibold">Offline</span>
                </>
              )}
            </Badge>
          </div>
        </div>
      )}

      {/* Messages */}
      <ScrollArea ref={scrollRef} className="flex-1 p-4 h-[500px]">
        <div className="space-y-4">
          {messages.map(message => (
            <MessageBubble key={message.id} message={message} />
          ))}
          {isLoading && (
            <div className="flex items-start gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 ring-1 ring-purple-500/20 mt-1 shadow-sm">
                <Bot className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1">
                <div className="bg-card border border-border/50 rounded-2xl p-4 inline-block shadow-sm">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-purple-600 dark:text-purple-400" />
                    <span className="text-sm text-muted-foreground font-medium">Thinking...</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="p-4 border-t border-border/50 bg-gradient-to-r from-transparent to-primary/5">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('ragChat.askQuestion')}
            disabled={isLoading || !isConnected}
            className="flex-1 h-11 rounded-xl border-border/50 focus-visible:ring-primary/50 shadow-sm bg-card"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || isLoading || !isConnected}
            size="icon"
            className="h-11 w-11 rounded-xl shadow-sm bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user';

  return (
    <div className={cn("flex items-start gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300", isUser && "flex-row-reverse")}>
      <div className={cn(
        "p-2.5 rounded-xl mt-1 shadow-sm transition-all",
        isUser 
          ? "bg-gradient-to-br from-teal-500/20 to-teal-600/20 ring-1 ring-teal-500/30" 
          : "bg-gradient-to-br from-purple-500/10 to-pink-500/10 ring-1 ring-purple-500/20"
      )}>
        {isUser ? (
          <User className="w-5 h-5 text-teal-600 dark:text-teal-400" />
        ) : (
          <Bot className="w-5 h-5 text-purple-600 dark:text-purple-400" />
        )}
      </div>

      <div className={cn("flex-1 space-y-2", isUser && "flex flex-col items-end")}>
        <div className={cn(
          "rounded-2xl p-4 max-w-[85%] inline-block shadow-sm transition-all",
          isUser 
            ? "bg-teal-500 dark:bg-teal-400 text-white ring-1 ring-teal-400/50" 
            : "bg-card border border-border/50"
        )}>
          {isUser ? (
            <p className="text-sm font-medium whitespace-pre-wrap leading-relaxed">{message.content}</p>
          ) : (
            <>
              {message.content ? (
                <MarkdownRenderer content={message.content} className="text-sm leading-relaxed" />
              ) : message.streaming ? (
                <span className="text-muted-foreground text-sm">...</span>
              ) : null}
              {message.streaming && (
                <div className="mt-3 flex items-center gap-2 text-xs opacity-70 pt-2 border-t border-border/30">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span className="font-medium">Streaming response...</span>
                </div>
              )}
            </>
          )}
        </div>

        <p className={cn(
          "text-xs font-medium px-2",
          isUser ? "text-teal-600/70 dark:text-teal-400/70" : "text-muted-foreground"
        )}>
          {message.timestamp.toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
          })}
        </p>
      </div>
    </div>
  );
}
